/**
 * TUVI - Lista de Proyectos
 * Visualización de todos los proyectos registrados
 */

import React, { useState, useEffect } from 'react';
import { matchesAnyField } from '../../utils/searchHelper';
import { semaforoCronograma } from '../../utils/semaforoCronograma';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import * as XLSX from 'xlsx';
import './ListaProyectos.css';

const ListaProyectos = ({ onModuleChange, onVerDetalle }) => {
  const { error: showError, success: showSuccess } = useNotification();
  const { user, tienePermiso } = useAuth();
  // La exportacion se habilita por usuario desde Acceso > Usuarios.
  const puedeExportar = tienePermiso('proyectos', 'lista-exportar');
  const isAdmin = user?.rol === 'admin';

  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  // Orden por fecha de entrada a "En Ejecución": null (sin orden) | 'desc' | 'asc'
  const [sortEjec, setSortEjec] = useState(null);

  // Estado para modal de confirmación de eliminación
  const [modalEliminar, setModalEliminar] = useState({ visible: false, proyecto: null });
  const [eliminando, setEliminando] = useState(false);

  // Estado para modal de crear proyecto manual
  const [modalCrear, setModalCrear] = useState(false);
  const [creando, setCreando] = useState(false);
  const [formNuevo, setFormNuevo] = useState({
    nombre_cliente: '', cedula: '', telefono: '', correo: '',
    proyecto_nombre: '', apartamento: '', direccion: '',
    area_m2: '', habitaciones: '', banos: '', valor_final: '',
  });

  useEffect(() => {
    cargarProyectos();
  }, []);

  const cargarProyectos = async () => {
    try {
      setLoading(true);
      const data = await client.query(`{
        proyectos {
          id fecha_cotizacion nombre_cliente cedula telefono correo
          proyecto_nombre apartamento comercial residente area_m2 habitaciones banos
          fecha_entrega_estimada subtotal aiu iva valor_final
          estado estado_cartera monto_pagado porcentaje_pagado fecha_en_ejecucion fecha_inicio_cronograma
          compromisos_abiertos compromisos_total ffe_recepcion
          actualizaciones_total actualizaciones_pendientes
        }
      }`);
      setProyectos(data.proyectos || []);
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cambiar estado de proyecto (actualización optimista, sin recargar la página)
  const cambiarEstado = async (proyectoId, nuevoEstado) => {
    // Capturar estado anterior por si hay que revertir
    const estadoAnterior = proyectos.find(p => p.id === proyectoId)?.estado;
    // Actualización local inmediata
    setProyectos(prev => prev.map(p => p.id === proyectoId ? { ...p, estado: nuevoEstado } : p));
    try {
      const data = await client.mutate(`
        mutation($id: Int!, $estado: String!) {
          actualizarEstadoProyecto(id: $id, estado: $estado) { success message }
        }
      `, { id: proyectoId, estado: nuevoEstado });
      if (!data.actualizarEstadoProyecto.success) {
        // Revertir si falló
        setProyectos(prev => prev.map(p => p.id === proyectoId ? { ...p, estado: estadoAnterior } : p));
        throw new Error(data.actualizarEstadoProyecto.message);
      }
      // Los entregados y liquidados viven en su propio módulo: al aplicar ese
      // estado, el proyecto sale de esta lista.
      if (nuevoEstado === 'entregado_liquidado') {
        setProyectos(prev => prev.filter(p => p.id !== proyectoId));
        showSuccess('Proyecto movido al módulo Entregados y Liquidados');
      }
    } catch (err) {
      // Revertir en caso de error de red
      setProyectos(prev => prev.map(p => p.id === proyectoId ? { ...p, estado: estadoAnterior } : p));
      showError('Error al cambiar estado: ' + err.message);
      console.error('Error:', err);
    }
  };

  // Eliminar proyecto (solo admin)
  const handleEliminarProyecto = async () => {
    if (!modalEliminar.proyecto || !isAdmin) return;

    setEliminando(true);
    try {
      const data = await client.mutate(`
        mutation($id: Int!) {
          eliminarProyecto(id: $id) { success message }
        }
      `, { id: modalEliminar.proyecto.id });

      if (data.eliminarProyecto.success) {
        showSuccess(`Proyecto "${modalEliminar.proyecto.proyecto_nombre}" eliminado correctamente`);
        setModalEliminar({ visible: false, proyecto: null });
        cargarProyectos();
      } else {
        throw new Error(data.eliminarProyecto.message);
      }
    } catch (err) {
      showError('Error al eliminar: ' + err.message);
      console.error('Error:', err);
    } finally {
      setEliminando(false);
    }
  };

  // Abrir modal de confirmación
  const confirmarEliminar = (proyecto, e) => {
    e.stopPropagation();
    setModalEliminar({ visible: true, proyecto });
  };

  // Crear proyecto manual
  const handleCrearProyecto = async (e) => {
    e.preventDefault();
    if (!formNuevo.nombre_cliente.trim()) { showError('Ingrese el nombre del cliente'); return; }
    if (!formNuevo.proyecto_nombre.trim()) { showError('Ingrese el nombre del proyecto'); return; }
    setCreando(true);
    try {
      const data = await client.mutate(`
        mutation($input: CrearProyectoInput!) {
          crearProyecto(input: $input) { success message proyecto_id }
        }
      `, {
        input: {
          proyecto: {
            nombre_cliente: formNuevo.nombre_cliente.trim(),
            proyecto_nombre: formNuevo.proyecto_nombre.trim(),
            cedula: formNuevo.cedula.trim() || null,
            telefono: formNuevo.telefono.trim() || null,
            correo: formNuevo.correo.trim() || null,
            direccion: formNuevo.direccion.trim() || null,
            apartamento: formNuevo.apartamento.trim() || null,
            area_m2: formNuevo.area_m2 ? parseFloat(formNuevo.area_m2) : null,
            habitaciones: formNuevo.habitaciones ? parseInt(formNuevo.habitaciones) : null,
            banos: formNuevo.banos ? parseInt(formNuevo.banos) : null,
            valor_final: formNuevo.valor_final ? parseFloat(formNuevo.valor_final) : null,
            estado: 'en_ejecucion',
          },
        },
      });
      if (data.crearProyecto.success) {
        showSuccess('Proyecto creado exitosamente');
        setModalCrear(false);
        setFormNuevo({ nombre_cliente: '', cedula: '', telefono: '', correo: '', proyecto_nombre: '', apartamento: '', direccion: '', area_m2: '', habitaciones: '', banos: '', valor_final: '' });
        cargarProyectos();
      } else {
        showError(data.crearProyecto.message || 'Error al crear proyecto');
      }
    } catch (err) {
      showError('Error: ' + err.message);
    } finally {
      setCreando(false);
    }
  };

  // Filtrar proyectos
  const proyectosFiltrados = proyectos.filter(proyecto => {
    // Filtro por estado (o por proyectos con compromisos registrados en Novedades,
    // incluidos los ya cumplidos: el indicador es permanente)
    if (filtro === 'con_compromisos') {
      if (!(proyecto.compromisos_total > 0)) return false;
    } else if (filtro !== 'todos' && proyecto.estado !== filtro) {
      return false;
    }

    // Búsqueda por texto (flexible: ignora tildes, mayúsculas, espacios)
    return matchesAnyField(proyecto, ['proyecto_nombre', 'nombre_cliente', 'apartamento', 'cedula', 'telefono', 'correo'], busqueda);
  });

  // Orden opcional por fecha de "En Ejecución" (los que no tienen fecha quedan al final)
  if (sortEjec) {
    proyectosFiltrados.sort((a, b) => {
      // Se ordena por la fecha de inicio del cronograma (la misma que se muestra)
      const ta = a.fecha_inicio_cronograma ? new Date(a.fecha_inicio_cronograma + 'T00:00:00').getTime() : null;
      const tb = b.fecha_inicio_cronograma ? new Date(b.fecha_inicio_cronograma + 'T00:00:00').getTime() : null;
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return sortEjec === 'asc' ? ta - tb : tb - ta;
    });
  }

  // Ciclo del orden: sin orden → más reciente primero → más antiguo primero → sin orden
  const toggleSortEjec = () => {
    setSortEjec(prev => (prev === null ? 'desc' : prev === 'desc' ? 'asc' : null));
  };

  // Fecha + hora de entrada a ejecución (ej. "12 jun 2026 · 3:45 p. m.")
  // Calcular estadísticas por estado (basado en proyectos filtrados por búsqueda)
  const calcularEstadisticas = () => {
    const proyectosBusqueda = proyectos.filter(proyecto =>
      matchesAnyField(proyecto, ['proyecto_nombre', 'nombre_cliente', 'apartamento', 'cedula', 'telefono', 'correo'], busqueda)
    );

    const stats = {
      todos: { cantidad: proyectosBusqueda.length, valor: 0 },
      vendido: { cantidad: 0, valor: 0 },
      contratado_no_iniciado: { cantidad: 0, valor: 0 },
      en_ejecucion: { cantidad: 0, valor: 0 },
      entregado_con_detalles: { cantidad: 0, valor: 0 },
      cobrado_liquidado: { cantidad: 0, valor: 0 },
      entregado_liquidado: { cantidad: 0, valor: 0 },
      con_compromisos: { cantidad: 0 }
    };

    proyectosBusqueda.forEach(proyecto => {
      const valor = parseFloat(proyecto.valor_final) || 0;
      stats.todos.valor += valor;
      if (proyecto.compromisos_total > 0) stats.con_compromisos.cantidad += 1;

      if (stats[proyecto.estado]) {
        stats[proyecto.estado].cantidad += 1;
        stats[proyecto.estado].valor += valor;
      }
    });

    return stats;
  };

  const estadisticas = calcularEstadisticas();

  // Formatear moneda
  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  // Exporta a Excel exactamente lo que se está viendo en la lista: las mismas
  // columnas y respetando el filtro de búsqueda y el de estado activos.
  const exportarExcel = () => {
    if (proyectosFiltrados.length === 0) {
      showError('No hay proyectos para exportar con los filtros actuales');
      return;
    }

    const ETIQUETA_ESTADO = {
      vendido: 'Vendido', contratado: 'Contratado', contratado_no_iniciado: 'Contratado no iniciado',
      en_ejecucion: 'En Ejecución', entregado_con_detalles: 'Entregado con Detalles',
      entregado_liquidado: 'Entregado y Liquidado', cobrado_liquidado: 'Cobrado y Liquidado',
      proyecto_especial: 'Proyecto Especial',
    };
    const ETIQUETA_CARTERA = { al_dia: 'Al día', atrasado: 'Atrasado', sin_cartera: 'Sin cartera' };

    const filas = proyectosFiltrados.map(p => ({
      'Proyecto': p.proyecto_nombre || '',
      'Cliente': p.nombre_cliente || '',
      'Cédula': p.cedula || '',
      'Apartamento': p.apartamento || '',
      'Comercial': p.comercial || '',
      'Residente': p.residente || '',
      'Área (m²)': p.area_m2 != null ? Number(p.area_m2) : '',
      'Valor Final': Number(p.valor_final || 0),
      'Pagado': Number(p.monto_pagado || 0),
      '% Pagado': Number(Number(p.porcentaje_pagado || 0).toFixed(1)),
      'Estado': ETIQUETA_ESTADO[p.estado] || p.estado || '',
      'Cartera': ETIQUETA_CARTERA[p.estado_cartera] || p.estado_cartera || '',
      'En Ejecución Desde': p.fecha_inicio_cronograma || '',
    }));

    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [
      { wch: 30 }, { wch: 28 }, { wch: 14 }, { wch: 16 }, { wch: 20 }, { wch: 22 },
      { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 10 }, { wch: 22 }, { wch: 12 }, { wch: 18 },
    ];
    // Los montos como moneda y el área con decimales
    const rango = XLSX.utils.decode_range(ws['!ref']);
    for (let f = 1; f <= rango.e.r; f++) {
      for (const col of [7, 8]) {
        const c = ws[XLSX.utils.encode_cell({ r: f, c: col })];
        if (c) c.z = '"$"#,##0';
      }
      const a = ws[XLSX.utils.encode_cell({ r: f, c: 6 })];
      if (a) a.z = '#,##0.00';
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Proyectos');
    const hoy = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Proyectos_TUVI_${hoy}.xlsx`);
    showSuccess(`${filas.length} proyecto(s) exportado(s)`);
  };

  // Formatear fecha
  const formatDate = (fecha) => {
    if (!fecha) return '-';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Badge de estado
  const EstadoBadge = ({ estado }) => {
    const estados = {
      'vendido': { label: 'Vendido', color: 'teal' },
      'contratado_no_iniciado': { label: 'Contratado', color: 'blue' },
      'en_ejecucion': { label: 'En Ejecución', color: 'green' },
      'entregado_con_detalles': { label: 'Entregado con Detalles', color: 'yellow' },
      'cobrado_liquidado': { label: 'Entregado y en Cobro', color: 'purple' },
      'entregado_liquidado': { label: 'Entregado y Liquidado', color: 'orange' }
    };

    const estadoInfo = estados[estado] || { label: estado, color: 'gray' };

    return (
      <span className={`estado-badge ${estadoInfo.color}`}>
        {estadoInfo.label}
      </span>
    );
  };

  // Badge de estado de cartera
  const CarteraBadge = ({ estadoCartera }) => {
    const isAlDia = estadoCartera === 'al_dia';
    return (
      <span className={`cartera-badge ${isAlDia ? 'al-dia' : 'atrasado'}`}>
        {isAlDia ? 'Al día' : 'Atrasado'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="lista-proyectos-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando proyectos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lista-proyectos-container">
        <div className="error-message">
          <h3>Error al cargar proyectos</h3>
          <p>{error}</p>
          <button onClick={cargarProyectos} className="btn-retry">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lista-proyectos-container">
      {/* Header */}
      <div className="proyectos-header">
        <div className="header-left">
          <h2>Proyectos</h2>
          <p className="subtitle">
            {proyectosFiltrados.length} de {proyectos.length} proyectos
          </p>
        </div>
        <div className="header-acciones">
          {puedeExportar && (
            <button className="btn-exportar-proy" onClick={exportarExcel} title="Exportar a Excel lo que se ve en la lista">
              Exportar Excel
            </button>
          )}
          <button
            className="btn-nuevo"
            onClick={() => setModalCrear(true)}
          >
            + Nuevo Proyecto
          </button>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="filtros-section">
        <div className="busqueda-box">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar proyecto, cliente o apartamento..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Indicadores de Estadísticas por Estado */}
      <div className="estadisticas-cards">
        <div
          className={`stat-card todos ${filtro === 'todos' ? 'active' : ''}`}
          onClick={() => setFiltro('todos')}
        >
          <div className="stat-header">
            <span className="stat-icon">📊</span>
            <span className="stat-title">Todos</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{estadisticas.todos.cantidad}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor">{formatMoney(estadisticas.todos.valor)}</span>
          </div>
        </div>

        <div
          className={`stat-card vendido ${filtro === 'vendido' ? 'active' : ''}`}
          onClick={() => setFiltro('vendido')}
        >
          <div className="stat-header">
            <span className="stat-icon">🏷️</span>
            <span className="stat-title">Vendidos</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{estadisticas.vendido.cantidad}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor">{formatMoney(estadisticas.vendido.valor)}</span>
          </div>
        </div>

        <div
          className={`stat-card contratado ${filtro === 'contratado_no_iniciado' ? 'active' : ''}`}
          onClick={() => setFiltro('contratado_no_iniciado')}
        >
          <div className="stat-header">
            <span className="stat-icon">📝</span>
            <span className="stat-title">Contratados</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{estadisticas.contratado_no_iniciado.cantidad}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor">{formatMoney(estadisticas.contratado_no_iniciado.valor)}</span>
          </div>
        </div>

        <div
          className={`stat-card ejecucion ${filtro === 'en_ejecucion' ? 'active' : ''}`}
          onClick={() => setFiltro('en_ejecucion')}
        >
          <div className="stat-header">
            <span className="stat-icon">🔨</span>
            <span className="stat-title">En Ejecución</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{estadisticas.en_ejecucion.cantidad}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor">{formatMoney(estadisticas.en_ejecucion.valor)}</span>
          </div>
        </div>

        <div
          className={`stat-card detalles ${filtro === 'entregado_con_detalles' ? 'active' : ''}`}
          onClick={() => setFiltro('entregado_con_detalles')}
        >
          <div className="stat-header">
            <span className="stat-icon">📋</span>
            <span className="stat-title">Entregado con Detalles</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{estadisticas.entregado_con_detalles.cantidad}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor">{formatMoney(estadisticas.entregado_con_detalles.valor)}</span>
          </div>
        </div>

        <div
          className={`stat-card cobrado ${filtro === 'cobrado_liquidado' ? 'active' : ''}`}
          onClick={() => setFiltro('cobrado_liquidado')}
        >
          <div className="stat-header">
            <span className="stat-icon">💰</span>
            <span className="stat-title">Entregado y en Cobro</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{estadisticas.cobrado_liquidado.cantidad}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor">{formatMoney(estadisticas.cobrado_liquidado.valor)}</span>
          </div>
        </div>

        <div
          className={`stat-card compromisos ${filtro === 'con_compromisos' ? 'active' : ''}`}
          onClick={() => setFiltro(filtro === 'con_compromisos' ? 'todos' : 'con_compromisos')}
          title="Proyectos con compromisos registrados en Novedades (vigentes y cumplidos)"
        >
          <div className="stat-header">
            <span className="stat-icon">⚠️</span>
            <span className="stat-title">Con compromisos</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{estadisticas.con_compromisos.cantidad}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor stat-valor-nota">Novedades registradas</span>
          </div>
        </div>

      </div>

      {/* Tabla de Proyectos */}
      {proyectosFiltrados.length === 0 ? (
        <div className="empty-state">
          <h3>No se encontraron proyectos</h3>
          <p>
            {busqueda || filtro !== 'todos'
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'Comienza creando tu primer proyecto desde un archivo Excel'}
          </p>
        </div>
      ) : (
        <div className="tabla-wrapper">
          <table className="proyectos-table">
            <thead>
              <tr>
                <th>Proyecto</th>
                <th>Cliente</th>
                <th>Apartamento</th>
                <th>Comercial</th>
                <th>Residente</th>
                <th>Área (m²)</th>
                <th>Valor Final</th>
                <th>Pagado</th>
                <th>Estado</th>
                <th>Cartera</th>
                <th
                  className="th-sort-ejec"
                  onClick={toggleSortEjec}
                  title="Ordenar por fecha de inicio del cronograma"
                >
                  En Ejecución Desde{' '}
                  <span className="sort-ind">{sortEjec === 'asc' ? '▲' : sortEjec === 'desc' ? '▼' : '⇅'}</span>
                </th>
                <th>Cambio Estado</th>
                {isAdmin && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {proyectosFiltrados.map((proyecto) => (
                <tr
                  key={proyecto.id}
                  onClick={() => {
                    if (onVerDetalle) {
                      onVerDetalle(proyecto.id);
                    }
                  }}
                  className={`proyecto-row ${proyecto.compromisos_total > 0 ? 'con-novedades' : ''}`}
                >
                  <td>
                    <div className="proyecto-info">
                      <span className="proyecto-nombre">{proyecto.proyecto_nombre}</span>
                      {proyecto.compromisos_total > 0 && (
                        <span
                          className="badge-novedades"
                          title={`Compromisos pactados con el cliente en Novedades: ${proyecto.compromisos_total} registrado${proyecto.compromisos_total === 1 ? '' : 's'}, ${proyecto.compromisos_abiertos || 0} vigente${proyecto.compromisos_abiertos === 1 ? '' : 's'}`}
                        >
                          ⚠ {proyecto.compromisos_total} {proyecto.compromisos_total === 1 ? 'novedad' : 'novedades'}
                        </span>
                      )}
                      {/* Actualizaciones: pendientes de firma (ámbar) o solo registradas (azul) */}
                      {proyecto.actualizaciones_pendientes > 0 ? (
                        <span className="badge-actualizaciones pendiente" title={`${proyecto.actualizaciones_pendientes} actualización(es) pendiente(s) de firma del cliente, de ${proyecto.actualizaciones_total} registrada(s)`}>
                          ✎ {proyecto.actualizaciones_pendientes} {proyecto.actualizaciones_pendientes === 1 ? 'actualización' : 'actualizaciones'} sin firmar
                        </span>
                      ) : proyecto.actualizaciones_total > 0 ? (
                        <span className="badge-actualizaciones" title="Actualizaciones registradas, todas aceptadas por el cliente">
                          ✎ {proyecto.actualizaciones_total} {proyecto.actualizaciones_total === 1 ? 'actualización' : 'actualizaciones'}
                        </span>
                      ) : null}
                      {/* Recibo del material de la Ficha FFE por el residente (proyectos en ejecución) */}
                      {proyecto.estado === 'en_ejecucion' && proyecto.ffe_recepcion === 'pendiente' && (
                        <span className="badge-ffe-recepcion pendiente" title="El residente todavía no ha aceptado el recibo del material de la Ficha FFE">
                          FFE sin aceptar
                        </span>
                      )}
                      {proyecto.estado === 'en_ejecucion' && proyecto.ffe_recepcion === 'con_faltantes' && (
                        <span className="badge-ffe-recepcion faltantes" title="El residente recibió el material de la Ficha FFE con faltantes">
                          FFE con faltantes
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="cliente-info">
                      <span className="cliente-nombre">{proyecto.nombre_cliente}</span>
                      <span className="cliente-cedula">{proyecto.cedula}</span>
                    </div>
                  </td>
                  <td>
                    <span className="apartamento-badge">{proyecto.apartamento || '-'}</span>
                  </td>
                  <td>
                    <span className="equipo-nombre">{proyecto.comercial || <span className="equipo-vacio">—</span>}</span>
                  </td>
                  <td>
                    <span className="equipo-nombre">{proyecto.residente || <span className="equipo-vacio">—</span>}</span>
                  </td>
                  <td>
                    <span className="area-value">{proyecto.area_m2} m²</span>
                  </td>
                  <td>
                    <span className="valor-final">{formatMoney(proyecto.valor_final)}</span>
                  </td>
                  <td>
                    {proyecto.monto_pagado > 0 || proyecto.porcentaje_pagado > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span className="valor-final" style={{ color: '#065f46', fontWeight: 600 }}>{formatMoney(proyecto.monto_pagado || 0)}</span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{Number(proyecto.porcentaje_pagado || 0).toFixed(1)}%</span>
                      </div>
                    ) : (
                      <span style={{ color: '#cbd5e1' }}>—</span>
                    )}
                  </td>
                  <td>
                    <EstadoBadge estado={proyecto.estado} />
                  </td>
                  <td>
                    <CarteraBadge estadoCartera={proyecto.estado_cartera} />
                  </td>
                  <td>
                    {(() => {
                      // El conteo arranca desde la fecha de inicio del CRONOGRAMA
                      const ini = proyecto.fecha_inicio_cronograma;
                      if (!ini) return <span style={{ color: '#cbd5e1' }}>—</span>;
                      const d = new Date(ini + 'T00:00:00');
                      const fechaTxt = isNaN(d.getTime()) ? ini : d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
                      const sem = semaforoCronograma(ini);
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.85rem' }}>{fechaTxt}</span>
                          {sem && (
                            <span
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px',
                                borderRadius: 999, color: sem.color, background: sem.bg,
                                border: `1px solid ${sem.border}`, whiteSpace: 'nowrap',
                              }}
                              title={`${sem.dias} días desde el inicio del cronograma · ${sem.label} (45 = amarillo, 60 = rojo)`}
                            >
                              ● {sem.dias} días
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="acciones" onClick={(e) => e.stopPropagation()}>
                      <select
                        className="select-estado"
                        value={proyecto.estado}
                        onChange={(e) => cambiarEstado(proyecto.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        title="Cambiar estado"
                      >
                        <option value="en_ejecucion">En Ejecución</option>
                        <option value="entregado_con_detalles">Entregado con Detalles</option>
                        <option value="cobrado_liquidado">Entregado y en Cobro</option>
                        <option value="entregado_liquidado">Entregado y Liquidado</option>
                      </select>
                    </div>
                  </td>
                  {isAdmin && (
                    <td>
                      <button
                        className="btn-eliminar-proyecto"
                        onClick={(e) => confirmarEliminar(proyecto, e)}
                        title="Eliminar proyecto"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {modalEliminar.visible && (
        <div className="modal-overlay" onClick={() => !eliminando && setModalEliminar({ visible: false, proyecto: null })}>
          <div className="modal-eliminar" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header eliminar">
              <h3>Eliminar Proyecto</h3>
              <button
                className="btn-cerrar"
                onClick={() => !eliminando && setModalEliminar({ visible: false, proyecto: null })}
                disabled={eliminando}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-icon">⚠️</div>
              <p className="warning-text">
                ¿Estás seguro de que deseas eliminar el proyecto?
              </p>
              <div className="proyecto-a-eliminar">
                <strong>{modalEliminar.proyecto?.proyecto_nombre}</strong>
                <span>Cliente: {modalEliminar.proyecto?.nombre_cliente}</span>
                <span>Apartamento: {modalEliminar.proyecto?.apartamento}</span>
              </div>
              <p className="danger-text">
                Esta acción eliminará permanentemente:
              </p>
              <ul className="lista-eliminacion">
                <li>Todos los capítulos y actividades del proyecto</li>
                <li>Todo el cronograma asociado</li>
                <li>La hoja de vida en cartera</li>
                <li>Todas las facturas asociadas al proyecto</li>
                <li>Todos los registros de caja menor</li>
              </ul>
              <p className="danger-text bold">Esta acción NO se puede deshacer.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancelar"
                onClick={() => setModalEliminar({ visible: false, proyecto: null })}
                disabled={eliminando}
              >
                Cancelar
              </button>
              <button
                className="btn-eliminar-confirmar"
                onClick={handleEliminarProyecto}
                disabled={eliminando}
              >
                {eliminando ? 'Eliminando...' : 'Sí, Eliminar Proyecto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear Proyecto */}
      {modalCrear && (
        <div className="modal-overlay-proy" onClick={() => setModalCrear(false)}>
          <div className="modal-crear-proy" onClick={e => e.stopPropagation()}>
            <div className="modal-crear-header">
              <h3>Nuevo Proyecto</h3>
              <button className="modal-close-btn" onClick={() => setModalCrear(false)}>X</button>
            </div>
            <form onSubmit={handleCrearProyecto}>
              <div className="modal-crear-body">
                <div className="form-section-title">Datos del Proyecto</div>
                <div className="form-row-2">
                  <div className="form-group-proy">
                    <label>Nombre del Proyecto *</label>
                    <input type="text" value={formNuevo.proyecto_nombre} onChange={e => setFormNuevo(p => ({ ...p, proyecto_nombre: e.target.value }))} required />
                  </div>
                  <div className="form-group-proy">
                    <label>Apartamento</label>
                    <input type="text" value={formNuevo.apartamento} onChange={e => setFormNuevo(p => ({ ...p, apartamento: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group-proy">
                    <label>Dirección</label>
                    <input type="text" value={formNuevo.direccion} onChange={e => setFormNuevo(p => ({ ...p, direccion: e.target.value }))} placeholder="Dirección del proyecto" />
                  </div>
                  <div className="form-group-proy">
                    <label>Valor del Contrato</label>
                    <input type="number" value={formNuevo.valor_final} onChange={e => setFormNuevo(p => ({ ...p, valor_final: e.target.value }))} placeholder="0" step="0.01" />
                  </div>
                </div>
                <div className="form-row-3">
                  <div className="form-group-proy">
                    <label>Área m²</label>
                    <input type="number" value={formNuevo.area_m2} onChange={e => setFormNuevo(p => ({ ...p, area_m2: e.target.value }))} step="0.01" />
                  </div>
                  <div className="form-group-proy">
                    <label>Habitaciones</label>
                    <input type="number" value={formNuevo.habitaciones} onChange={e => setFormNuevo(p => ({ ...p, habitaciones: e.target.value }))} />
                  </div>
                  <div className="form-group-proy">
                    <label>Baños</label>
                    <input type="number" value={formNuevo.banos} onChange={e => setFormNuevo(p => ({ ...p, banos: e.target.value }))} />
                  </div>
                </div>

                <div className="form-section-title" style={{ marginTop: 16 }}>Datos del Cliente</div>
                <div className="form-row-2">
                  <div className="form-group-proy">
                    <label>Nombre del Cliente *</label>
                    <input type="text" value={formNuevo.nombre_cliente} onChange={e => setFormNuevo(p => ({ ...p, nombre_cliente: e.target.value }))} required />
                  </div>
                  <div className="form-group-proy">
                    <label>Cédula</label>
                    <input type="text" value={formNuevo.cedula} onChange={e => setFormNuevo(p => ({ ...p, cedula: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group-proy">
                    <label>Teléfono</label>
                    <input type="text" value={formNuevo.telefono} onChange={e => setFormNuevo(p => ({ ...p, telefono: e.target.value }))} />
                  </div>
                  <div className="form-group-proy">
                    <label>Correo</label>
                    <input type="email" value={formNuevo.correo} onChange={e => setFormNuevo(p => ({ ...p, correo: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-crear-footer">
                <button type="button" className="btn-cancelar-proy" onClick={() => setModalCrear(false)} disabled={creando}>Cancelar</button>
                <button type="submit" className="btn-crear-proy" disabled={creando}>{creando ? 'Creando...' : 'Crear Proyecto'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaProyectos;
